const ratesData = require("./data.json");

// ── Core Functions ─────────────────────────────────────────

/**
 * Get the zone for an Australian postcode.
 * Returns "Zone A" | "Zone B" | "Zone C" or null if not found.
 *
 * @param {string} postcode - Australian postcode (e.g. "3000")
 * @returns {string|null} Zone name or null if not found
 */
const getZone = (postcode) => {
  return ratesData.postcodeToZone[postcode] ?? null;
};

/**
 * Calculate volumetric weight from dimensions (cm).
 * Formula: L × W × H / 4000
 *
 * @param {number} lengthCm - Length in centimeters
 * @param {number} widthCm - Width in centimeters
 * @param {number} heightCm - Height in centimeters
 * @returns {number} Volumetric weight in kg
 */
const volumetricWeight = (lengthCm, widthCm, heightCm) => {
  return (lengthCm * widthCm * heightCm) / ratesData.volumetricDivisor;
};

/**
 * Determine the applicable weight tier.
 * Returns the smallest tier >= the given weight, or null if over max.
 * Tiers: 0.5, 1, 3, 5, 10, 15, 20, 25 kg
 *
 * @param {number} weightKg - Weight in kilograms
 * @returns {number|null} Weight tier or null if exceeds max
 */
const getWeightTier = (weightKg) => {
  for (const tier of ratesData.weightThresholdsKg) {
    if (weightKg <= tier) return tier;
  }
  return null;
};

/**
 * Look up the shipping rate for a zone and weight tier.
 *
 * @param {string} zone - Zone name (e.g. "Zone A")
 * @param {number} tierKg - Weight tier in kg
 * @returns {number|null} Shipping rate in AUD or null if not found
 */
const getRate = (zone, tierKg) => {
  return ratesData.rateCard[zone]?.[String(tierKg)] ?? null;
};

/**
 * Calculate shipping cost for a single consignment.
 *
 * @param {string} postcode - Australian postcode (e.g. "3000")
 * @param {number} weightKg - Actual package weight in kg
 * @param {number} lengthCm - Package length in cm
 * @param {number} widthCm - Package width in cm
 * @param {number} heightCm - Package height in cm
 * @returns {Object} ShippingResult with details or returns 99999 for errors
 * @property {string} zone - Zone name
 * @property {number} actualWeightKg - Actual weight
 * @property {number} volumetricWeightKg - Calculated volumetric weight
 * @property {number} chargeableWeightKg - Weight used for billing
 * @property {number} weightTierKg - Weight tier applied
 * @property {number} costAUD - Final cost in AUD
 */
const calculateShipping = (postcode, weightKg, lengthCm, widthCm, heightCm) => {
  // 1. Zone lookup
  const zone = getZone(postcode);
  if (!zone) {
    // Postcode not found - return error value
    return 99999;
  }

  // 2. Volumetric vs actual weight
  const volWeight = volumetricWeight(lengthCm, widthCm, heightCm);
  const chargeable = Math.max(weightKg, volWeight);

  // 3. Weight tier
  const tier = getWeightTier(chargeable);
  if (!tier) {
    // Weight exceeds maximum
    return 99999;
  }

  // 4. Rate lookup
  const cost = getRate(zone, tier);
  if (cost === null) {
    // Rate not found
    return 99999;
  }

  return {
    zone,
    actualWeightKg: weightKg,
    volumetricWeightKg: Math.round(volWeight * 100) / 100,
    chargeableWeightKg: Math.round(chargeable * 100) / 100,
    weightTierKg: tier,
    costAUD: cost,
  };
};

/**
 * Simplified version that returns just the cost (for compatibility with other calculators)
 *
 * @param {string} postcode - Australian postcode
 * @param {number} weightKg - Actual package weight in kg
 * @param {number} lengthCm - Package length in cm
 * @param {number} widthCm - Package width in cm
 * @param {number} heightCm - Package height in cm
 * @returns {number} Shipping cost in AUD or 99999 for errors
 */
const calculateShippingCost = (postcode, weightKg, lengthCm, widthCm, heightCm) => {
  const result = calculateShipping(postcode, weightKg, lengthCm, widthCm, heightCm);
  return typeof result === 'number' ? result : result.costAUD;
};

/**
 * Batch calculate shipping for multiple consignments.
 *
 * @param {Array} orders - Array of order objects with postcode, weightKg, lengthCm, widthCm, heightCm
 * @returns {Array} Array of shipping results with optional error field
 */
const calculateBatch = (orders) => {
  return orders.map((order) => {
    try {
      const result = calculateShipping(
        order.postcode,
        order.weightKg,
        order.lengthCm,
        order.widthCm,
        order.heightCm
      );

      if (typeof result === 'number') {
        // Error occurred, result is 99999
        return {
          zone: "",
          actualWeightKg: order.weightKg,
          volumetricWeightKg: 0,
          chargeableWeightKg: 0,
          weightTierKg: 0,
          costAUD: result,
          error: "Calculation error"
        };
      }

      return result;
    } catch (e) {
      return {
        zone: "",
        actualWeightKg: order.weightKg,
        volumetricWeightKg: 0,
        chargeableWeightKg: 0,
        weightTierKg: 0,
        costAUD: 99999,
        error: e.message,
      };
    }
  });
};

/**
 * Get maximum supported weight
 * @returns {number} Maximum weight in kg
 */
const getMaxWeight = () => {
  return ratesData.maxWeightKg;
};

/**
 * Get all available zones
 * @returns {Array<string>} Array of zone names
 */
const getAvailableZones = () => {
  return Object.keys(ratesData.rateCard);
};

/**
 * Get volumetric divisor used for calculations
 * @returns {number} Volumetric divisor
 */
const getVolumetricDivisor = () => {
  return ratesData.volumetricDivisor;
};

module.exports = {
  calculateShipping,
  calculateShippingCost,
  calculateBatch,
  getZone,
  volumetricWeight,
  getWeightTier,
  getRate,
  getMaxWeight,
  getAvailableZones,
  getVolumetricDivisor
};

// ── Usage Examples ─────────────────────────────────────────
//
// Single order - detailed result
// const result = calculateShipping("3000", 2.5, 30, 20, 15);
// console.log(result);
// => { zone: "Zone A", actualWeightKg: 2.5, volumetricWeightKg: 2.25,
//      chargeableWeightKg: 2.5, weightTierKg: 3, costAUD: 4.85 }
//
// Single order - just the cost
// const cost = calculateShippingCost("3000", 2.5, 30, 20, 15);
// console.log(cost); // => 4.85
//
// Batch processing
// const results = calculateBatch([
//   { postcode: "3000", weightKg: 0.3, lengthCm: 10, widthCm: 10, heightCm: 5 },
//   { postcode: "2000", weightKg: 8, lengthCm: 40, widthCm: 30, heightCm: 20 },
//   { postcode: "4870", weightKg: 15, lengthCm: 50, widthCm: 40, heightCm: 30 },
// ]);
// console.log(results);
